import { Grid, Skeleton, Theme } from '@mui/material';
import { useDialog } from 'muibox';
import { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import SextforceMetricsCampaignCountersGraph from './SextforceMetricsCampaignCountersGraph';
import SextforceMetricsCampaignSelector from './SextforceMetricsCampaignSelector';
import SextforceMetricsCampaignSubscribedAtGraph from './SextforceMetricsCampaignSubscribedAtGraph';
// import SextforceMetricsSuspiciousUsersList from '../../SextforceMetricsSuspiciousUsersList';
import DeferMountWhenVisible from '../../../../../../components/common/DeferMountWhenVisible';
import useSextforceMetricsCountersLastOfEveryMonth from '../../../../../../hooks/useSextforceMetricsCountersLastOfEveryMonth';
import useSextforceMetricsSubscribedAtVsTotalSalesDetails from '../../../../../../hooks/useSextforceMetricsSubscribedAtVsTotalSalesDetails';
import useSextforceMetricsUsersSalesDetails from '../../../../../../hooks/useSextforceMetricsUsersSalesDetails';
import { OnlyFansSubscriber } from '../../../../../../hooks/useSubscriber';
import useSubscribers from '../../../../../../hooks/useSubscribers';
import { SettingsContext } from '../../../../../../store/SettingsContext';
import { UserContext } from '../../../../../../store/UserContext';
import { d2f, handleHttpError, handleHttpErrorResponse } from '../../../../../../utils/common';
import SextforceMetricsChargebacksByProductGraph from '../../SextforceMetricsChargebacksByProductGraph';
import SextforceMetricsCrossReferenceEarnings from '../../SextforceMetricsCrossReferenceEarnings';
import SextforceMetricsEarningsByIntervalGraph from '../../SextforceMetricsEarningsByIntervalGraph';
import SextforceMetricsSalesByProductGraph from '../../SextforceMetricsSalesByProductGraph';
import SextforceMetricsSubscribedAtVsSalesSummary from '../../SextforceMetricsSubscribedAtVsSalesSummary';
import SextforceMetricsUsersIdScatterWithRiskLevelGraph from '../../SextforceMetricsUsersIdScatterWithRiskLevelGraph';
import SextforceMetricsUsersWithRiskLevelList from '../../SextforceMetricsUsersWithRiskLevelList';
import SextforceMetricsCampaignOverview from './SextforceMetricsCampaignOverview';

export interface TotalSales {
    total: number;
    totalWithoutSubscriptions: number;
    resubscription: number;
    resubscriptionCount: number;
    subscription: number;
    subscriptionCount: number;
    tip: number;
    tipCount: number;
    message: number;
    messageCount: number;
    post: number;
    postCount: number;
    stream: number;
    streamCount: number;
    totalChargebacks: number;
    totalChargebacksCount: number;
    totalChargebacksWithoutSubscriptions: number;
    totalChargebacksWithoutSubscriptionsCount: number;
    totalChargebacksMessage: number;
    totalChargebacksMessageCount: number;
    totalChargebacksResubscription: number;
    totalChargebacksResubscriptionCount: number;
    totalChargebacksTip: number;
    totalChargebacksTipCount: number;
    totalChargebacksSubscription: number;
    totalChargebacksSubscriptionCount: number;
    totalChargebacksPost: number;
    totalChargebacksPostCount: number;
    totalChargebacksStream: number;
    totalChargebacksStreamCount: number;
}

type Props = {
    subscriber: OnlyFansSubscriber | null;
    timezone: string;
    theme: Theme;
};

const SextforceMetricsCampaign = (props: Props) => {
    const { subscriber, timezone, theme } = props;

    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const [searchParams] = useSearchParams();
    const dialog = useDialog();
    // const queryClient = useQueryClient();

    const [promoCampaignId, setPromoCampaignId] = useState<string>(params.metricId || '');
    const [promoCampaignsShowInactive, setPromoCampaignsShowInactive] = useState<boolean>(
        searchParams.has('includeInactive')
            ? searchParams.get('includeInactive') === 'true'
            : localStorage.getItem('sextforceMetricsCampaignsOverviewSelectorType') === 'true' || false,
    );
    const [promoCampaignData, setPromoCampaignData] = useState<any | null>();
    const [promoCampaignTotalSales, setPromoCampaignTotalSales] = useState<TotalSales>({
        total: 0,
        totalWithoutSubscriptions: 0,
        resubscription: 0,
        resubscriptionCount: 0,
        subscription: 0,
        subscriptionCount: 0,
        tip: 0,
        tipCount: 0,
        message: 0,
        messageCount: 0,
        post: 0,
        postCount: 0,
        stream: 0,
        streamCount: 0,
        totalChargebacks: 0,
        totalChargebacksCount: 0,
        totalChargebacksWithoutSubscriptions: 0,
        totalChargebacksWithoutSubscriptionsCount: 0,
        totalChargebacksMessage: 0,
        totalChargebacksMessageCount: 0,
        totalChargebacksResubscription: 0,
        totalChargebacksResubscriptionCount: 0,
        totalChargebacksTip: 0,
        totalChargebacksTipCount: 0,
        totalChargebacksSubscription: 0,
        totalChargebacksSubscriptionCount: 0,
        totalChargebacksPost: 0,
        totalChargebacksPostCount: 0,
        totalChargebacksStream: 0,
        totalChargebacksStreamCount: 0,
    });
    const [showEarningsWithSubscriptions, setShowEarningsWithSubscriptions] = useState<boolean>(
        localStorage.getItem('showEarningsWithSubscriptions') !== null
            ? localStorage.getItem('showEarningsWithSubscriptions') === 'true'
            : true,
    );
    const [showEarningsAsGross, setShowEarningsAsGross] = useState<boolean>(
        localStorage.getItem('showEarningsAsGross') !== null ? localStorage.getItem('showEarningsAsGross') === 'true' : true,
    );

    // Cross-reference block is defer-mounted; without this, useSubscribers only ran after scroll (no request until then).
    useSubscribers();

    // const [isSavingSettings, setIsSavingSettings] = useState<boolean>(false);

    // Fetch Report
    const fetchPromoCampgains = async (status: boolean): Promise<any> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber) {
            const query: string = `${settingsContext.routes.metrics.base}${params.userId}/promocampaigns?${
                typeof status === 'boolean'
                    ? new URLSearchParams({
                          status: status ? 'true' : 'false',
                      })
                    : ''
            }`;

            const data = await fetch(query, {
                method: 'get',
                headers: {
                    Authorization: userContext.jwtToken,
                    apiKey: settingsContext.apiKey,
                },
            })
                .then(async response => {
                    if (response.ok) {
                        return response.json();
                    } else {
                        handleHttpErrorResponse(response, dialog);
                    }
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);
                });

            return data;
        }

        return [];
    };

    const {
        data: promoCampaigns,
        isLoading: promoCampaignsLoading,
        isFetched: promoCampaignsFetched,
    } = useQuery(
        ['metricsPromoCampaings', promoCampaignsShowInactive, params.userId],
        () => fetchPromoCampgains(promoCampaignsShowInactive),
        {
            refetchOnWindowFocus: false,
            // Stale time 1 hour
            staleTime: 60 * 1000 * 60,
            enabled: subscriber ? true : false,
        },
    );

    useEffect(() => {
        if (promoCampaigns && promoCampaignId !== '') {
            const selectedCampaign = promoCampaigns.find((campaign: any) => campaign._id === promoCampaignId);

            setPromoCampaignData(selectedCampaign || null);
        }
    }, [promoCampaignId, promoCampaigns]);

    // Fetch Promo Campaign Counters
    const fetchPromoCampgainCounters = async (): Promise<
        {
            _id: string;
            payload: {
                countSubscribers: number;
                countTransitions: number;
            };
        }[]
    > => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber) {
            const query: string = `${settingsContext.routes.metrics.base}${params.userId}/promocampaigns/${promoCampaignId}/counters`;

            const data = await fetch(query, {
                method: 'get',
                headers: {
                    Authorization: userContext.jwtToken,
                    apiKey: settingsContext.apiKey,
                },
            })
                .then(async response => {
                    if (response.ok) {
                        return response.json();
                    } else {
                        handleHttpErrorResponse(response, dialog);
                    }
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);

                    return [];
                });

            return data;
        }

        return [];
    };

    const {
        data: promoCampaignCounters,
        isLoading: promoCampaignCountersLoading,
        isFetched: promoCampaignCountersFetched,
    } = useQuery([`metricsPromoCampaingCounters_${promoCampaignId}`, promoCampaignId, params.userId], () => fetchPromoCampgainCounters(), {
        refetchOnWindowFocus: false,
        // Stale time 1 hour
        staleTime: 60 * 1000 * 60,
        enabled: Boolean(subscriber && promoCampaignId !== '' && promoCampaignsFetched),
    });

    const {
        data: promoCampaignSales,
        isLoading: promoCampaignSalesLoading,
        isFetched: promoCampaignSalesFetched,
    } = useSextforceMetricsUsersSalesDetails(
        params.userId,
        promoCampaignId,
        promoCampaignData?.type,
        undefined,
        undefined,
        promoCampaignCountersFetched,
    );

    // Fetch Promo Campaign Subscribed At vs. Sales details (after per-user sales)
    const {
        data: promoCampaignSubscribedAtVsSales,
        isLoading: promoCampaignSubscribedAtVsSalesLoading,
        isFetched: promoCampaignSubscribedAtFetched,
    } = useSextforceMetricsSubscribedAtVsTotalSalesDetails(
        subscriber?._id,
        'campaign',
        promoCampaignId,
        timezone,
        promoCampaignSalesFetched,
    );

    const { data: countersLastOfEveryMonth, isLoading: countersLastOfEveryMonthLoading } = useSextforceMetricsCountersLastOfEveryMonth(
        params.userId,
        promoCampaignData?._id,
        promoCampaignData?.type,
        promoCampaignSubscribedAtFetched,
    );

    const promoCampaignCountersLoadingEffective =
        Boolean(subscriber && promoCampaignId !== '') && (!promoCampaignsFetched || promoCampaignCountersLoading);

    const salesStaggerPrereqs =
        Boolean(userContext.jwtToken && settingsContext.apiKey && params.userId && promoCampaignId) && promoCampaignsFetched;
    const promoCampaignSalesLoadingEffective =
        promoCampaignSalesLoading || (salesStaggerPrereqs && (!promoCampaignData?.type || !promoCampaignCountersFetched));

    const subscribedAtStaggerPrereqs = Boolean(
        userContext.jwtToken && settingsContext.apiKey && params.userId && subscriber?._id && promoCampaignId,
    );
    const promoCampaignSubscribedAtVsSalesLoadingEffective =
        promoCampaignSubscribedAtVsSalesLoading ||
        (subscribedAtStaggerPrereqs && Boolean(promoCampaignData?.type && promoCampaignCountersFetched) && !promoCampaignSalesFetched);

    const lastOfMonthStaggerPrereqs = Boolean(
        userContext.jwtToken && settingsContext.apiKey && params.userId && promoCampaignData?._id && promoCampaignData?.type,
    );
    const countersLastOfEveryMonthLoadingEffective =
        countersLastOfEveryMonthLoading || (lastOfMonthStaggerPrereqs && !promoCampaignSubscribedAtFetched);

    // Calculate total sales for the selected campaign
    useEffect(() => {
        if (!promoCampaignSalesFetched || promoCampaignSalesLoading) {
            return;
        }
        if (promoCampaignSales && promoCampaignSales.length > 0) {
            // Initialize accumulator object
            const totals = {
                total: 0,
                totalWithoutSubscriptions: 0,
                resubscription: 0,
                resubscriptionCount: 0,
                subscription: 0,
                subscriptionCount: 0,
                message: 0,
                messageCount: 0,
                post: 0,
                postCount: 0,
                stream: 0,
                streamCount: 0,
                tip: 0,
                tipCount: 0,
                totalChargebacks: 0,
                totalChargebacksCount: 0,
                totalChargebacksWithoutSubscriptions: 0,
                totalChargebacksWithoutSubscriptionsCount: 0,
                totalChargebacksMessage: 0,
                totalChargebacksMessageCount: 0,
                totalChargebacksResubscription: 0,
                totalChargebacksResubscriptionCount: 0,
                totalChargebacksTip: 0,
                totalChargebacksTipCount: 0,
                totalChargebacksSubscription: 0,
                totalChargebacksSubscriptionCount: 0,
                totalChargebacksPost: 0,
                totalChargebacksPostCount: 0,
                totalChargebacksStream: 0,
                totalChargebacksStreamCount: 0,
            };

            // Single loop through the array
            for (const row of promoCampaignSales) {
                // Process all fields directly
                if (row.total) totals.total += d2f(row.total);
                if (row.totalWithoutSubscriptions) totals.totalWithoutSubscriptions += d2f(row.totalWithoutSubscriptions);

                if (row.totalResubscription) {
                    const value = d2f(row.totalResubscription);
                    totals.resubscription += value;
                    if (value > 0) totals.resubscriptionCount += 1;
                }

                if (row.totalSubscription) {
                    const value = d2f(row.totalSubscription);
                    totals.subscription += value;
                    if (value > 0) totals.subscriptionCount += 1;
                }

                if (row.totalMessage) {
                    const value = d2f(row.totalMessage);
                    totals.message += value;
                    if (value > 0) totals.messageCount += 1;
                }

                if (row.totalPost) {
                    const value = d2f(row.totalPost);
                    totals.post += value;
                    if (value > 0) totals.postCount += 1;
                }

                if (row.totalStream) {
                    const value = d2f(row.totalStream);
                    totals.stream += value;
                    if (value > 0) totals.streamCount += 1;
                }

                if (row.totalTip) {
                    const value = d2f(row.totalTip);
                    totals.tip += value;
                    if (value > 0) totals.tipCount += 1;
                }

                if (row.totalChargebacks) {
                    const value = d2f(row.totalChargebacks);
                    totals.totalChargebacks += value;
                    if (value > 0) totals.totalChargebacksCount += 1;
                }

                if (row.totalChargebacksWithoutSubscriptions) {
                    const value = d2f(row.totalChargebacksWithoutSubscriptions);
                    totals.totalChargebacksWithoutSubscriptions += value;
                    if (value > 0) totals.totalChargebacksWithoutSubscriptionsCount += 1;
                }

                if (row.totalChargebacksMessage) {
                    const value = d2f(row.totalChargebacksMessage);
                    totals.totalChargebacksMessage += value;
                    if (value > 0) totals.totalChargebacksMessageCount += 1;
                }

                if (row.totalChargebacksResubscription) {
                    const value = d2f(row.totalChargebacksResubscription);
                    totals.totalChargebacksResubscription += value;
                    if (value > 0) totals.totalChargebacksResubscriptionCount += 1;
                }

                if (row.totalChargebacksTip) {
                    const value = d2f(row.totalChargebacksTip);
                    totals.totalChargebacksTip += value;
                    if (value > 0) totals.totalChargebacksTipCount += 1;
                }

                if (row.totalChargebacksSubscription) {
                    const value = d2f(row.totalChargebacksSubscription);
                    totals.totalChargebacksSubscription += value;
                    if (value > 0) totals.totalChargebacksSubscriptionCount += 1;
                }

                if (row.totalChargebacksPost) {
                    const value = d2f(row.totalChargebacksPost);
                    totals.totalChargebacksPost += value;
                    if (value > 0) totals.totalChargebacksPostCount += 1;
                }

                if (row.totalChargebacksStream) {
                    const value = d2f(row.totalChargebacksStream);
                    totals.totalChargebacksStream += value;
                    if (value > 0) totals.totalChargebacksStreamCount += 1;
                }
            }

            // Apply Math.trunc and multiply by 100
            const newTotalSales: TotalSales = {
                total: Math.trunc(totals.total * 100),
                totalWithoutSubscriptions: Math.trunc(totals.totalWithoutSubscriptions * 100),
                resubscription: Math.trunc(totals.resubscription * 100),
                resubscriptionCount: totals.resubscriptionCount,
                subscription: Math.trunc(totals.subscription * 100),
                subscriptionCount: totals.subscriptionCount,
                message: Math.trunc(totals.message * 100),
                messageCount: totals.messageCount,
                post: Math.trunc(totals.post * 100),
                postCount: totals.postCount,
                stream: Math.trunc(totals.stream * 100),
                streamCount: totals.streamCount,
                tip: Math.trunc(totals.tip * 100),
                tipCount: totals.tipCount,
                totalChargebacks: Math.trunc(totals.totalChargebacks * 100),
                totalChargebacksCount: totals.totalChargebacksCount,
                totalChargebacksWithoutSubscriptions: Math.trunc(totals.totalChargebacksWithoutSubscriptions * 100),
                totalChargebacksWithoutSubscriptionsCount: totals.totalChargebacksWithoutSubscriptionsCount,
                totalChargebacksMessage: Math.trunc(totals.totalChargebacksMessage * 100),
                totalChargebacksMessageCount: totals.totalChargebacksMessageCount,
                totalChargebacksResubscription: Math.trunc(totals.totalChargebacksResubscription * 100),
                totalChargebacksResubscriptionCount: totals.totalChargebacksResubscriptionCount,
                totalChargebacksTip: Math.trunc(totals.totalChargebacksTip * 100),
                totalChargebacksTipCount: totals.totalChargebacksTipCount,
                totalChargebacksSubscription: Math.trunc(totals.totalChargebacksSubscription * 100),
                totalChargebacksSubscriptionCount: totals.totalChargebacksSubscriptionCount,
                totalChargebacksPost: Math.trunc(totals.totalChargebacksPost * 100),
                totalChargebacksPostCount: totals.totalChargebacksPostCount,
                totalChargebacksStream: Math.trunc(totals.totalChargebacksStream * 100),
                totalChargebacksStreamCount: totals.totalChargebacksStreamCount,
            };

            setPromoCampaignTotalSales(newTotalSales);
        } else {
            setPromoCampaignTotalSales({
                total: 0,
                totalWithoutSubscriptions: 0,
                resubscription: 0,
                resubscriptionCount: 0,
                subscription: 0,
                subscriptionCount: 0,
                message: 0,
                messageCount: 0,
                post: 0,
                postCount: 0,
                stream: 0,
                streamCount: 0,
                tip: 0,
                tipCount: 0,
                totalChargebacks: 0,
                totalChargebacksCount: 0,
                totalChargebacksWithoutSubscriptions: 0,
                totalChargebacksWithoutSubscriptionsCount: 0,
                totalChargebacksMessage: 0,
                totalChargebacksMessageCount: 0,
                totalChargebacksResubscription: 0,
                totalChargebacksResubscriptionCount: 0,
                totalChargebacksTip: 0,
                totalChargebacksTipCount: 0,
                totalChargebacksSubscription: 0,
                totalChargebacksSubscriptionCount: 0,
                totalChargebacksPost: 0,
                totalChargebacksPostCount: 0,
                totalChargebacksStream: 0,
                totalChargebacksStreamCount: 0,
            });
        }
    }, [promoCampaignSales, promoCampaignSalesLoading, promoCampaignSalesFetched, promoCampaignId]);

    return (
        <>
            <Grid container flexGrow={1} spacing={1} alignItems="center" mb={1}>
                <Grid item xs={12} sm>
                    {promoCampaignsLoading ? (
                        <Skeleton />
                    ) : (
                        <SextforceMetricsCampaignSelector
                            promoCampaigns={promoCampaigns}
                            promoCampaignsLoading={promoCampaignsLoading}
                            promoCampaignId={promoCampaignId}
                            setPromoCampaignId={setPromoCampaignId}
                            showInactive={promoCampaignsShowInactive}
                            setShowInactive={setPromoCampaignsShowInactive}
                            timezone={timezone}
                            theme={theme}
                        />
                    )}
                </Grid>
            </Grid>

            <DeferMountWhenVisible minHeight={320} rootMargin="200px 0px">
                <SextforceMetricsCampaignOverview
                    subscriber={subscriber}
                    promoCampaign={promoCampaignData}
                    promoCampaignCounters={{ metricsDatas: promoCampaignCounters }}
                    promoCampaignCountersLoading={promoCampaignCountersLoadingEffective}
                    promoCampaignTotalSales={promoCampaignTotalSales}
                    promoCampaignSales={promoCampaignSales}
                    promoCampaignSalesLoading={promoCampaignSalesLoadingEffective}
                    showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                    setShowEarningsWithSubscriptions={setShowEarningsWithSubscriptions}
                    showEarningsAsGross={showEarningsAsGross}
                    setShowEarningsAsGross={setShowEarningsAsGross}
                    goalSubs={promoCampaignData && promoCampaignData.settings ? promoCampaignData.settings.goalSubs : undefined}
                    goalEarnings={promoCampaignData && promoCampaignData.settings ? promoCampaignData.settings.goalSpent : undefined}
                    theme={theme}
                />
            </DeferMountWhenVisible>

            <DeferMountWhenVisible minHeight={280} rootMargin="200px 0px">
                <SextforceMetricsCrossReferenceEarnings
                    metric={promoCampaignData}
                    metricType="campaign"
                    showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                    showEarningsAsGross={showEarningsAsGross}
                    theme={theme}
                />
            </DeferMountWhenVisible>

            <DeferMountWhenVisible minHeight={360} rootMargin="200px 0px">
                <SextforceMetricsCampaignCountersGraph
                    metricId={promoCampaignData?._id || ''}
                    promoCampaignCounters={{ metricsDatas: promoCampaignCounters }}
                    promoCampaignCountersLoading={promoCampaignCountersLoadingEffective}
                    timezone={timezone}
                    theme={theme}
                />
            </DeferMountWhenVisible>

            <DeferMountWhenVisible minHeight={360} rootMargin="200px 0px">
                <SextforceMetricsCampaignSubscribedAtGraph
                    metricId={promoCampaignId}
                    promoCampaignSubscribedAtVsSales={promoCampaignSubscribedAtVsSales}
                    promoCampaignSubscribedAtVsSalesLoading={promoCampaignSubscribedAtVsSalesLoadingEffective}
                    showEarnings={true}
                    showEarningsAsGross={showEarningsAsGross}
                    showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                    timezone={timezone}
                    theme={theme}
                />
            </DeferMountWhenVisible>

            <DeferMountWhenVisible minHeight={300} rootMargin="200px 0px">
                <SextforceMetricsSubscribedAtVsSalesSummary
                    metricId={promoCampaignId}
                    promoCampaignSubscribedAtVsSales={promoCampaignSubscribedAtVsSales}
                    promoCampaignSubscribedAtVsSalesLoading={promoCampaignSubscribedAtVsSalesLoadingEffective}
                    countersLastOfEveryMonth={countersLastOfEveryMonth}
                    countersLastOfEveryMonthLoading={countersLastOfEveryMonthLoadingEffective}
                    showEarningsAsGross={showEarningsAsGross}
                    showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                    timezone={timezone}
                />
            </DeferMountWhenVisible>

            <DeferMountWhenVisible minHeight={360} rootMargin="200px 0px">
                <SextforceMetricsEarningsByIntervalGraph
                    subscriber={subscriber}
                    metricId={promoCampaignId}
                    metricType={promoCampaignData && promoCampaignData.type ? promoCampaignData.type : undefined}
                    showEarningsAsGross={showEarningsAsGross}
                    showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                    timezone={timezone}
                    theme={theme}
                />
            </DeferMountWhenVisible>

            <DeferMountWhenVisible minHeight={320} rootMargin="200px 0px">
                <SextforceMetricsSalesByProductGraph
                    totalSales={promoCampaignTotalSales}
                    totalSalesLoading={promoCampaignSalesLoadingEffective}
                    showEarningsAsGross={showEarningsAsGross}
                    theme={theme}
                />
            </DeferMountWhenVisible>

            <DeferMountWhenVisible minHeight={320} rootMargin="200px 0px">
                <SextforceMetricsChargebacksByProductGraph
                    totalSales={promoCampaignTotalSales}
                    totalSalesLoading={promoCampaignSalesLoadingEffective}
                    showEarningsAsGross={showEarningsAsGross}
                    theme={theme}
                />
            </DeferMountWhenVisible>

            <DeferMountWhenVisible minHeight={400} rootMargin="200px 0px">
                <SextforceMetricsUsersIdScatterWithRiskLevelGraph users={promoCampaignSales || []} />
            </DeferMountWhenVisible>

            <DeferMountWhenVisible minHeight={400} rootMargin="240px 0px">
                <SextforceMetricsUsersWithRiskLevelList
                    metricId={promoCampaignId}
                    promoCampaignTotalSales={promoCampaignSales}
                    promoCampaignTotalSalesLoading={promoCampaignSalesLoadingEffective}
                    showEarningsAsGross={showEarningsAsGross}
                    showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                    metricStartDate={
                        promoCampaignData && promoCampaignData.payload && promoCampaignData.payload.createdAt
                            ? promoCampaignData.payload.createdAt
                            : undefined
                    }
                    metricType={promoCampaignData && promoCampaignData.type ? promoCampaignData.type : undefined}
                    timezone={timezone}
                    theme={theme}
                />
            </DeferMountWhenVisible>
            {/* <SextforceMetricsCampaignUsers
                metricId={promoCampaignId}
                promoCampaignTotalSales={promoCampaignSales}
                promoCampaignTotalSalesLoading={promoCampaignSalesLoading}
                showEarningsAsGross={showEarningsAsGross}
                showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                metricStartDate={
                    promoCampaignData && promoCampaignData.payload && promoCampaignData.payload.createdAt
                        ? promoCampaignData.payload.createdAt
                        : undefined
                }
                metricType={promoCampaignData && promoCampaignData.type ? promoCampaignData.type : undefined}
                closeUsers={closeUsers}
                timezone={timezone}
                theme={theme}
            /> */}
        </>
    );
};

export default SextforceMetricsCampaign;
